"use client";

import Explainable from "@ishvaram/ui/Explainable";

import SEOAgentBridge from "@/components/SEOAgentBridge";
import { depthPreviewFor } from "@/lib/depth-preview";
import { canShowPrompt } from "@/lib/feedback/promptCeiling";
import { getSEOIntentConfig } from "@/lib/seo-intents";

/**
 * Graph-membership anchor for the client modules that dozens of route surfaces
 * import independently. Turbopack emits the root layout's client graph once
 * into site-wide chunks and every route chunk group then EXCLUDES those
 * modules (parent-chunk-group dedup) — but two routes importing the same
 * client component still get separate full copies when their overall module
 * sets differ. Anchoring the widest commons here therefore removes one copy
 * per mounting route from the bundle (measured 2026-08-22: seo-intents-data
 * ×12, phosphor IconBase ×7, promptCeiling ×6, SEOAgentBridge ×6,
 * depth-preview ×7 across the top-20 route chunks).
 *
 * This is the rule-46 delivery-runtime shape: install the heavy shared graph
 * ONCE from the root layout; route islands keep their own thin mounts.
 *
 * Budget discipline: every module anchored here ships to all 333 routes.
 * Only add a module that is already duplicated across MANY route chunk
 * groups (site-wide cost < sum of removed copies) — never a route-local
 * heavyweight like the 54 KB nakshatra presentation data (that class gets a
 * shared client-side next/dynamic boundary instead, e.g. NakshatraStudioLazy).
 */
const HOISTED_COMMONS = [
  // SEOAgentBridge pulls seo-intents(-data), purchase-intent-score,
  // customer-language, and browser-storage — its whole static client graph.
  SEOAgentBridge,
  // Explicit anchor for the 18.9 KB intent config data, independent of the
  // bridge's own import list.
  getSEOIntentConfig,
  // @ishvaram/ui/Explainable is the module the duplicated chunks actually
  // share: it carries phosphor-react's IconBase (15.5 KB) plus Modal.web.
  // pnpm strict deps forbid importing phosphor-react from apps/web directly.
  Explainable,
  canShowPrompt,
  depthPreviewFor,
] as const;

export default function SharedClientCommons() {
  // Renders nothing. Mounting (not just importing) from the root layout is
  // what puts this module in the always-loaded client graph; referencing the
  // array keeps the imports live under any future export-level tree shaking.
  void HOISTED_COMMONS.length;
  return null;
}
